<template>
  <div>
    <Placeholder v-if="loading" class="h-35px" />
    <treeselect
      v-else
      v-model="model"
      :options="options"
      :load-options="loadOptions"
      :auto-load-root-options="!delayedLoading"
      :loading-text="translate('CATEGORY_FILTER.OPTIONS_LOADING')"
      :multiple="multiple"
      :limit="1"
      :limitText="(count) => translate('CATEGORY_FILTER.LIMIT_TEXT', { count })"
      :placeholder="placeholder || translate('CATEGORY_FILTER.PLACEHOLDER')"
      :class="{ 'treeselect-invalid': isInvalid }"
    >
      <template v-slot:value-label="{ node }">
        <div>
          {{ displayLabelWithEllipsis(node) }}
        </div>
      </template>
    </treeselect>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { LOAD_CHILDREN_OPTIONS } from "@zanmato/vue3-treeselect";
import legacyCategoryService from "@/core/services/api/category.service";
import Placeholder from "@/view/components/Shared/Placeholder.vue";
import useTranslate from "@/core/composables/translate";
import {
  getCategoryTree,
  getCategoryTreeSelect,
  loadDirectChildren,
} from "@/apps/shared/catalog/core/infrastructure/service/categoryService";
import { legacyCategoriesToModel } from "@/apps/dashboard/catalog/core/infrastructure/repository/mapper/categoryMapper";

const props = defineProps({
  modelValue: [String, Array],
  placeholder: String,
  isInvalid: { type: Boolean, default: false },
  withConnectedTrees: { type: Boolean, default: false },
  removeRootCategory: { type: Boolean, default: false },
  multiple: { type: Boolean, default: true },
  delayedLoading: { type: Boolean, default: true },
});

const model = defineModel();
defineEmits(["input"]);

const { translate } = useTranslate();

const options = ref(null);
const loading = ref(true);
const initialLabels = ref([]);

onMounted(async () => {
  try {
    let categoryOptions;
    if (props.withConnectedTrees) {
      categoryOptions = legacyCategoriesToModel(await legacyCategoryService.getConnectedCategoriesTree());
    } else if (props.removeRootCategory) {
      categoryOptions = (await getCategoryTree()).children;
    } else {
      categoryOptions = [await getCategoryTree()];
    }

    options.value = categoryOptions.map(getCategoryTreeSelect);
  } finally {
    loading.value = false;
  }
});

const displayLabelWithEllipsis = (node) => {
  const maxLength = 40;
  const label = node.raw.label ?? initialLabels.value.find(({ id }) => id === node.id)?.label ?? "";
  return label.length > maxLength ? `${label.substring(0, maxLength)}…` : label;
};

const loadOptions = async ({ action, parentNode }) => {
  if (action === LOAD_CHILDREN_OPTIONS) {
    await loadDirectChildren(parentNode).finally(() => (loading.value = false));
  }
};
</script>
